import { atom, map } from 'nanostores';
import { toast } from 'react-toastify';
import type { FileSystemAdapter } from './file-system-adapter';
import { generateChanges } from './generate-changes';
import { Snapshot } from './snapshot';
import type { FileSystemSnapshot } from './snapshot';
import { clientFetch } from '~/lib/.client/fetch';
import { SUPABASE_MIGRATIONS_FOLDER } from '~/lib/constants';
import type { FileMap } from '~/lib/stores/files';
import { workbenchStore } from '~/lib/stores/workbench';
import type { Message } from '~/types/chat';
import { invariant } from '~/utils/invariant';
import { createScopedLogger } from '~/utils/logger';
import { removeWorkDir } from '~/utils/workDir';

const logger = createScopedLogger('SnapshotManager');

interface SnapshotInfo {
  messageId: string;
  createdAt: string;
  title?: string;
  bookmarkedAt?: string | null;
}

type SnapshotsInfo = Record<string, Snapshot>;

class SnapshotManager {
  snapshots = map<SnapshotsInfo>({});

  /**
   * Whether a snapshot operation is currently in progress.
   */
  isLoadingSnapshot = atom<boolean>(false);

  async fetchSnapshots(projectId?: string, signal?: AbortSignal) {
    if (!projectId) {
      return;
    }

    logger.debug('Fetching snapshots');

    const response = await clientFetch(`/api/snapshot/${projectId}`, {
      signal,
    });

    const { snapshots } = await response.json<{ snapshots: SnapshotInfo[] }>();

    this.snapshots.set(
      snapshots.reduce(
        (result, snapshotInfo) => {
          const messageId = snapshotInfo.messageId;

          result[messageId] = new Snapshot(projectId, {
            ...snapshotInfo,
            createdAt: new Date(snapshotInfo.createdAt),
            bookmarkedAt: snapshotInfo.bookmarkedAt ? new Date(snapshotInfo.bookmarkedAt) : null,
            finalized: true,
          });

          return result;
        },
        { ...this.snapshots.get() },
      ),
    );
  }

  hasSnapshot(messageId?: string) {
    return messageId && this.snapshots.get()[messageId] !== undefined;
  }

  async create(projectId: string, messageId: string, files: Readonly<FileMap>, title?: string) {
    logger.debug('Creating snapshot');

    const snapshot = new Snapshot(projectId, {
      messageId,
      createdAt: new Date(),
      title,
    });

    await snapshot.finalize(files);

    this.snapshots.setKey(messageId, snapshot);

    return snapshot;
  }

  hydrate(projectId: string, messages: Message[]) {
    const snapshots: SnapshotsInfo = {};

    for (const message of messages) {
      if (message.snapshot) {
        const createdAt = message.createdAt ? new Date(message.createdAt) : new Date();

        snapshots[message.id] = new Snapshot(projectId, {
          messageId: message.id,
          createdAt,
          finalized: true,
        });
      }
    }

    this.snapshots.set(snapshots);
  }

  /**
   * Preview a snapshot using current workbench files.
   */
  async previewSnapshot(messageId: string, fs: FileSystemAdapter) {
    const files = workbenchStore.files.get();

    return this.#withFileListenerDisabled(async () => {
      await this.#preview(messageId, files, fs);
    }, false);
  }

  /**
   * Restore a snapshot using current workbench files.
   */
  async restoreSnapshot(messageId: string, fs: FileSystemAdapter) {
    const files = workbenchStore.files.get();
    return this.#restore(messageId, files, fs);
  }

  /**
   * Restore from a backup snapshot using current workbench files.
   */
  async restoreFromPreviewSession(
    { backupFiles, hasUserModifiedFiles }: { backupFiles: FileMap; hasUserModifiedFiles: boolean },
    fs: FileSystemAdapter,
  ) {
    this.isLoadingSnapshot.set(true);

    const currentFiles = workbenchStore.files.get();
    const backupSnapshot = this.#fileMapToSnapshot(backupFiles);

    try {
      await this.#applySnapshot(currentFiles, backupSnapshot, fs);
      workbenchStore.setHasUserModifiedFiles(hasUserModifiedFiles);
    } finally {
      this.isLoadingSnapshot.set(false);
    }
  }

  async #preview(messageId: string, files: FileMap, fs: FileSystemAdapter) {
    logger.debug('Preview');

    invariant(!this.isLoadingSnapshot.get(), 'Already checking out');

    try {
      this.isLoadingSnapshot.set(true);

      const snapshot = this.snapshots.get()[messageId];

      await snapshot.saving;

      const snapshotData = await snapshot.fetch();

      this.#applySnapshot(files, snapshotData, fs);
    } finally {
      this.isLoadingSnapshot.set(false);
    }
  }

  /**
   * Restore from a snapshot and keep all chat messages.
   */
  async #restore(messageId: string, files: FileMap, fs: FileSystemAdapter) {
    logger.debug('Restore');

    invariant(!this.isLoadingSnapshot.get(), 'Already checking out');

    try {
      this.isLoadingSnapshot.set(true);

      const snapshot = this.snapshots.get()[messageId];

      await snapshot.saving;

      const snapshotData = await snapshot.fetch();

      workbenchStore.setFileListenerEnabled(true);

      await this.#applySnapshot(files, snapshotData, fs);
    } finally {
      this.isLoadingSnapshot.set(false);
    }
  }

  /**
   * Checkout a snapshot and delete all chat messages newer than the snapshot.
   */
  async checkout(messageId: string, files: FileMap, fs: FileSystemAdapter) {
    logger.debug('Checkout');

    invariant(!this.isLoadingSnapshot.get(), 'Already checking out');

    try {
      this.isLoadingSnapshot.set(true);

      const snapshot = this.snapshots.get()[messageId];

      await snapshot.saving;

      const snapshotData = await snapshot.fetch();

      workbenchStore.setFileListenerEnabled(true);

      await this.#applySnapshot(files, snapshotData, fs);

      await this.deleteChatMessagesUntilSnapshot(snapshot);
    } finally {
      this.isLoadingSnapshot.set(false);
    }
  }

  /**
   * Delete all chat messages newer than the snapshot.
   */
  async deleteChatMessagesUntilSnapshot(snapshot: Snapshot) {
    return clientFetch(`/api/snapshot/${snapshot.projectId}/apply/${snapshot.messageId}`, { method: 'POST' });
  }

  async #applySnapshot(files: FileMap, snapshot: FileSystemSnapshot, fs: FileSystemAdapter) {
    logger.debug('Apply snapshot');

    const changes = generateChanges(files, snapshot);

    for (const { type, filePath, dirent } of changes) {
      switch (type) {
        case 'rmdir': {
          if (removeWorkDir(filePath) === SUPABASE_MIGRATIONS_FOLDER) {
            // we don't want to remove the supabase migration files
            continue;
          }

          invariant(dirent.type === 'folder', 'Expected folder');
          await fs.rmdir(filePath);
          break;
        }
        case 'mkdir': {
          invariant(dirent.type === 'folder', 'Expected folder');
          await fs.mkdir(filePath);
          break;
        }
        case 'writeFile': {
          invariant(dirent.type === 'file', 'Expected file');

          if (dirent.blobId) {
            dirent.isStackBlitzBinary = true;
          }

          await fs.writeFile(filePath, dirent);
          break;
        }
        case 'removeFile': {
          if (removeWorkDir(filePath).startsWith(`${SUPABASE_MIGRATIONS_FOLDER}/`)) {
            // we don't want to remove the supabase migration files
            continue;
          }

          invariant(dirent.type === 'file', 'Expected file');
          await fs.removeFile(filePath);
          break;
        }
      }
    }
  }

  #fileMapToSnapshot(fileMap: FileMap): FileSystemSnapshot {
    const snapshot: FileSystemSnapshot = {};

    for (const [filePath, file] of Object.entries(fileMap)) {
      if (!file) {
        continue;
      }

      const filename = removeWorkDir(filePath);

      if (file.type === 'folder') {
        snapshot[filename] = { type: 'folder' };
      } else {
        snapshot[filename] = {
          type: 'file',
          content: file.content,
          ...(file.isBinary && { isBinary: true }),
          ...(file.blobId && { blobId: file.blobId }),
        };
      }
    }

    return snapshot;
  }

  /**
   * Helper method to execute a function with file listeners disabled.
   * Re-enables listeners after execution, even if an error occurs.
   */
  async #withFileListenerDisabled<T>(fn: () => Promise<T>, reEnableAfter = true): Promise<T> {
    try {
      workbenchStore.setFileListenerEnabled(false);
      return await fn();
    } finally {
      if (reEnableAfter) {
        workbenchStore.setFileListenerEnabled(true);
      }
    }
  }

  async toggleBookmark(projectId: string, messageId: string, bookmarked: boolean) {
    const snapshots = this.snapshots.get();
    const snapshot = snapshots[messageId];

    let previousBookmarkedAt: Date | null | undefined;

    if (snapshot) {
      previousBookmarkedAt = snapshot.bookmarkedAt;
      snapshot.bookmarkedAt = bookmarked ? new Date() : null;
      this.snapshots.set({ ...snapshots });
    }

    try {
      const response = await clientFetch(`/api/snapshot/${projectId}/${messageId}/bookmark`, {
        method: 'POST',
        body: JSON.stringify({ bookmarked }),
      });

      if (!response.ok) {
        throw new Error('Failed to toggle bookmark');
      }
    } catch {
      if (snapshot) {
        snapshot.bookmarkedAt = previousBookmarkedAt;
        this.snapshots.set({ ...snapshots });
      }

      toast.error('Failed to bookmark');
    }
  }

  async updateTitle(projectId: string, messageId: string, title: string) {
    const snapshots = this.snapshots.get();
    const snapshot = snapshots[messageId];

    let previousTitle: string | undefined;

    if (snapshot) {
      previousTitle = snapshot.title;
      snapshot.title = title;
      this.snapshots.set({ ...snapshots });
    }

    try {
      const response = await clientFetch(`/api/snapshot/${projectId}/${messageId}/title`, {
        method: 'POST',
        body: JSON.stringify({ title }),
      });

      if (!response.ok) {
        throw new Error('Failed to update title');
      }
    } catch {
      if (snapshot) {
        snapshot.title = previousTitle;
        this.snapshots.set({ ...snapshots });
      }

      toast.error('Failed to update title');
    }
  }
}

export const snapshotManager = new SnapshotManager();

if (import.meta.env.DEV) {
  Object.assign(globalThis, { snapshotManager });
}
